import React, { useState } from 'react';
import { useQuery } from '@/apollo';
import {
  User,
  Users,
  Stethoscope,
  UserPlus,
  MapPin,
  Shield,
  Server,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  Calendar,
  Activity,
  Trash2,
  Ban
} from 'lucide-react';
import { 
  GET_LOGIN_ATTEMPTS, 
  GET_USER_ACTION_LOGS, 
  GET_PROVIDER_LOGS,
  GET_LOCATION_LOGS,
  GET_INSURANCE_LOGS,
  GET_CONDITION_LOGS,
  GET_SYSTEM_LOGS,
  GET_BLOCKED_EVENTS
} from '../../graphql/logs';
import { LogRetentionSettings } from './LogRetentionSettings';

// Type definitions for log data
interface LogUser {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
}

interface AuditLog {
  id: string;
  userId?: string;
  user?: LogUser;
  action: string;
  resource: string;
  resourceId?: string;
  details?: string | Record<string, unknown>;
  timestamp: string;
  success: boolean;
  ipAddress?: string;
  userAgent?: string;
}

interface LoginAttempt {
  id: string;
  userId?: string;
  user?: LogUser;
  email: string;
  success: boolean;
  ipAddress?: string;
  userAgent?: string;
  failureCode?: string;
  timestamp: string;
  sessionId?: string;
  location?: string;
  method?: string;
  type?: 'login';
}

interface SystemLog {
  id: string;
  level: string;
  category: string;
  message: string;
  details?: string | Record<string, unknown>;
  source?: string;
  timestamp: string;
  errorCode?: string;
  stackTrace?: string;
  metadata?: Record<string, unknown>;
}

interface LogsConnection {
  nodes: AuditLog[];
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Removed unused interfaces

// Dark scrollbar styles for the modal
const DARK_SCROLLBAR_STYLES = `
  .log-modal-scrollbar::-webkit-scrollbar {
    width: 10px;
  }
  .log-modal-scrollbar::-webkit-scrollbar-track {
    background: var(--color-bg-secondary);
    border-radius: 5px;
  }
  .log-modal-scrollbar::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 5px;
    border: 2px solid var(--color-bg-secondary);
  }
  .log-modal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-muted);
  }
  .log-modal-scrollbar::-webkit-scrollbar-corner {
    background: var(--color-bg-secondary);
  }
  /* Firefox scrollbar */
  .log-modal-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: var(--color-border) var(--color-bg-secondary);
  }
`;

interface LogsManagementProps {
  activeLogTab: string;
  setActiveLogTab: (tab: string) => void;
}

export const LogsManagement: React.FC<LogsManagementProps> = ({ activeLogTab, setActiveLogTab }) => {
  const [pagination] = useState({ first: 20 });
  type CombinedLog = (LoginAttempt & { type: 'login' }) | (AuditLog & { type: 'action' }) | (SystemLog & { type: 'system' });
  const [selectedLog, setSelectedLog] = useState<CombinedLog | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);

  const logTabs = [
    { id: 'user-activity', label: 'User Activity', icon: Activity },
    { id: 'blocked-events', label: 'Blocked Accesses', icon: Ban },
    { id: 'locations', label: 'Locations', icon: MapPin },
    { id: 'appointment-details', label: 'Appointment Details', icon: Calendar },
    { id: 'conditions', label: 'Conditions', icon: Stethoscope },
    { id: 'providers', label: 'Providers', icon: UserPlus },
    { id: 'insurances', label: 'Insurances', icon: Shield },
    { id: 'system', label: 'System Logs', icon: Server },
    { id: 'retention', label: 'Retention & Cleanup', icon: Trash2 }
  ];

  // Query hooks
  const { data: loginData, loading: loginLoading } = useQuery(GET_LOGIN_ATTEMPTS, {
    variables: { pagination },
    skip: activeLogTab !== 'user-activity',
    fetchPolicy: 'network-only'
  });

  const { data: userActionData, loading: userActionLoading } = useQuery(GET_USER_ACTION_LOGS, {
    variables: { pagination, filter: { category: 'USER_ACTION' } },
    skip: activeLogTab !== 'user-activity',
    fetchPolicy: 'network-only'
  });

  const { data: providerData, loading: providerLoading } = useQuery(GET_PROVIDER_LOGS, {
    variables: { pagination },
    skip: activeLogTab !== 'providers',
    fetchPolicy: 'network-only'
  });

  const { data: locationData, loading: locationLoading } = useQuery(GET_LOCATION_LOGS, {
    variables: { pagination },
    skip: activeLogTab !== 'locations',
    fetchPolicy: 'network-only'
  });

  const { data: insuranceData, loading: insuranceLoading } = useQuery(GET_INSURANCE_LOGS, {
    variables: { pagination },
    skip: activeLogTab !== 'insurances',
    fetchPolicy: 'network-only'
  });

  const { data: conditionData, loading: conditionLoading } = useQuery(GET_CONDITION_LOGS, {
    variables: { pagination },
    skip: activeLogTab !== 'conditions',
    fetchPolicy: 'network-only'
  });

  const { data: systemData, loading: systemLoading } = useQuery(GET_SYSTEM_LOGS, {
    variables: { pagination },
    skip: activeLogTab !== 'system',
    fetchPolicy: 'network-only'
  });

  const { data: blockedEventsData, loading: blockedEventsLoading } = useQuery(GET_BLOCKED_EVENTS, {
    variables: { 
      pagination, 
      filter: { 
        action: 'ACCESS_BLOCKED',
        success: false
      } 
    },
    skip: activeLogTab !== 'blocked-events',
    fetchPolicy: 'network-only'
  });


  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hour${Math.floor(diffInMinutes / 60) === 1 ? '' : 's'} ago`;
    return `${Math.floor(diffInMinutes / 1440)} day${Math.floor(diffInMinutes / 1440) === 1 ? '' : 's'} ago`;
  };

  const renderLogHeader = (title: string) => {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0 mb-4">
        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</h4>
          <div className="flex space-x-2">
          <button 
            className="px-3 py-1 text-sm rounded-lg flex items-center space-x-1 transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)',
              color: 'white'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          <button 
            className="px-3 py-1 text-sm rounded-lg flex items-center space-x-1 transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
            }}
          >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </button>
          </div>
        </div>
    );
  };

  const renderEmptyState = (message: string) => {
    return (
      <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
        {message}
      </div>
    );
  };

  const renderUserActivity = () => {
    const loginLogs: LoginAttempt[] = loginData?.loginAttempts?.nodes || [];
    const userActionLogs: AuditLog[] = userActionData?.auditLogs?.nodes || [];
    const isLoading = loginLoading || userActionLoading;
    
    if (isLoading) return <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>Loading user activity...</div>;
    
    // Combine and sort by timestamp (newest first)
    type CombinedLog = (LoginAttempt & { type: 'login' }) | (AuditLog & { type: 'action' });
    const allLogs: CombinedLog[] = [
      ...loginLogs.map((log: LoginAttempt) => ({ ...log, type: 'login' as const })),
      ...userActionLogs.map((log: AuditLog) => ({ ...log, type: 'action' as const }))
    ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    const handleRowClick = (log: CombinedLog) => {
      setSelectedLog(log);
      setShowLogModal(true);
    };
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {renderLogHeader('User Activity')}
        {allLogs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            {renderEmptyState('No user activity found')}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="overflow-y-auto dark-scrollbar flex-1">
              <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Activity</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Method</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>IP Address</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Time</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {allLogs.map((log: CombinedLog) => {
                    const userEmail = log.type === 'login' ? log.email : (log.user ? log.user.email : 'System');
                    const activity = log.type === 'login' 
                      ? (log.success ? 'Successful login' : `Failed login${log.failureCode ? ` (${log.failureCode})` : ''}`)
                      : log.action;
                    const isSuccess = log.type === 'login' ? log.success : log.success !== false;
                    const loginMethod = log.type === 'login' ? log.method : null;
                    const methodDisplay = loginMethod === 'entra-id' ? 'Entra ID' : loginMethod === 'password' ? 'Local' : (log.type === 'action' ? '-' : 'Unknown');
                    
                    return (
                      <tr
                        key={`${log.type}-${log.id}`}
                        onClick={() => handleRowClick(log)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid var(--color-border)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                            {log.type === 'login' ? (
                              <User className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                    ) : (
                              <Users className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                    )}
                            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{userEmail}</span>
                  </div>
                        </td>
                        <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>
                          {activity}
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {methodDisplay}
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {log.ipAddress || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {formatTimeAgo(log.timestamp)}
                        </td>
                        <td className="py-3 px-4">
                          {log.type === 'login' ? (
                            <span 
                              className="px-2 py-1 text-xs rounded-full inline-block"
                              style={{
                                backgroundColor: isSuccess ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                color: isSuccess ? 'var(--color-success)' : 'var(--color-error)'
                              }}
                            >
                              {isSuccess ? 'Success' : 'Failed'}
                            </span>
                          ) : (
                            <div className="flex items-center">
                              {isSuccess ? (
                                <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-success)' }} />
                              ) : (
                                <XCircle className="w-4 h-4" style={{ color: 'var(--color-error)' }} />
                              )}
                  </div>
                )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </div>
          )}
      </div>
    );
  };

  // Helper function to format key names
  const formatKeyName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Helper function to parse action and extract name
  const formatDetails = (details: string | Record<string, unknown> | undefined): React.ReactNode => {
    if (!details) return null;

    // If details is a string, try to parse it as JSON
    let parsedDetails: Record<string, unknown>;
    if (typeof details === 'string') {
      try {
        parsedDetails = JSON.parse(details);
      } catch {
        // If parsing fails, just return the string
        return (
          <div className="mt-1 p-3 rounded text-sm" style={{ 
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-primary)'
          }}>
            {details}
          </div>
        );
      }
    } else {
      parsedDetails = details;
    }

    // Check if we have original and changes for side-by-side comparison
    if (parsedDetails && typeof parsedDetails === 'object' && 'original' in parsedDetails && 'changes' in parsedDetails) {
      const original = parsedDetails['original'] as Record<string, any>;
      const changes = parsedDetails['changes'] as Record<string, any>;
      const changedKeys = Object.keys(changes);

      if (changedKeys.length === 0) return null;
    
    return (
        <div className="mt-3">
          <div className="text-sm font-semibold mb-4 pb-2 border-b" style={{ 
            color: 'var(--color-text-primary)',
            borderColor: 'var(--color-border)'
          }}>
            Changes Made
          </div>
      <div className="space-y-4">
            {changedKeys.map((key: string) => {
              const formattedKey = formatKeyName(key);
              const originalValue = original[key];
              const newValue = changes[key];
              const hasChanged = originalValue !== newValue;
              const originalDisplay = originalValue === null || originalValue === undefined 
                ? 'Not set' 
                : originalValue === '' 
                ? '(empty)' 
                : String(originalValue);
              const newDisplay = newValue === null || newValue === undefined 
                ? 'Not set' 
                : newValue === '' 
                ? '(empty)' 
                : String(newValue);

              return (
                <div key={key} className="rounded-lg overflow-hidden" style={{ 
                  backgroundColor: 'var(--color-bg-secondary)',
                  border: `1px solid ${hasChanged ? 'rgba(34, 197, 94, 0.3)' : 'var(--color-border)'}`
                }}>
                  <div className="px-4 py-2.5 border-b" style={{ 
                    backgroundColor: hasChanged ? 'rgba(34, 197, 94, 0.05)' : 'transparent',
                    borderColor: 'var(--color-border)'
                  }}>
                    <div className="flex items-center space-x-2">
                      {hasChanged && (
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
                      )}
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        {formattedKey}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2" style={{ 
                    borderLeft: '1px solid var(--color-border)',
                    borderRight: '1px solid var(--color-border)'
                  }}>
                    <div className="p-4" style={{ borderRight: '1px solid var(--color-border)' }}>
                      <div className="text-xs font-medium mb-2 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                        Original
                      </div>
                      <div className="text-sm p-3 rounded-md min-h-[2.5rem] flex items-center" style={{ 
                        backgroundColor: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border)'
                      }}>
                        <span className={originalValue === null || originalValue === undefined || originalValue === '' 
                          ? 'italic' 
                          : ''} 
                          style={{ 
                            color: (originalValue === null || originalValue === undefined || originalValue === '') 
                              ? 'var(--color-text-muted)' 
                              : 'var(--color-text-primary)'
                          }}>
                          {originalDisplay}
                  </span>
                      </div>
                </div>
                    <div className="p-4" style={{ 
                      backgroundColor: hasChanged ? 'rgba(34, 197, 94, 0.02)' : 'transparent'
                    }}>
                      <div className="text-xs font-medium mb-2 uppercase tracking-wide flex items-center space-x-1" style={{ color: 'var(--color-text-muted)' }}>
                        <span>New Value</span>
                        {hasChanged && (
                          <span className="text-xs px-1.5 py-0.5 rounded" style={{ 
                            backgroundColor: 'rgba(34, 197, 94, 0.2)',
                            color: 'var(--color-success)'
                          }}>
                            Changed
                          </span>
                )}
              </div>
                      <div className="text-sm p-3 rounded-md min-h-[2.5rem] flex items-center transition-all" style={{ 
                        backgroundColor: hasChanged ? 'rgba(34, 197, 94, 0.08)' : 'var(--color-bg-primary)',
                        color: hasChanged ? 'var(--color-success)' : 'var(--color-text-primary)',
                        border: hasChanged ? '2px solid var(--color-success)' : '1px solid var(--color-border)',
                        boxShadow: hasChanged ? '0 0 0 3px rgba(34, 197, 94, 0.1)' : 'none',
                        outline: 'none'
                      }}>
                        <span className={newValue === null || newValue === undefined || newValue === '' 
                          ? 'italic' 
                          : ''} 
                          style={{ 
                            color: (newValue === null || newValue === undefined || newValue === '') 
                              ? 'var(--color-text-muted)' 
                              : (hasChanged ? 'var(--color-success)' : 'var(--color-text-primary)')
                          }}>
                          {newDisplay}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
    }

    // Format based on the structure of the details
    const entries = Object.entries(parsedDetails);
    
    if (entries.length === 0) return null;
    
    return (
      <div className="mt-1 space-y-2">
        {entries.map(([key, value]) => {
          // Skip null/undefined values and skip original/changes if they were already handled
          if (value === null || value === undefined || key === 'original' || key === 'changes') return null;

          const formattedKey = formatKeyName(key);

          // Handle nested objects (like other metadata)
          if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            const nestedEntries = Object.entries(value as Record<string, unknown>);
            if (nestedEntries.length > 0) {
              return (
                <div key={key} className="p-3 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <div className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {formattedKey}
                  </div>
                  <div className="space-y-1 ml-2">
                    {nestedEntries.map(([nestedKey, nestedValue]) => {
                      const formattedNestedKey = formatKeyName(nestedKey);
                      return (
                        <div key={nestedKey} className="flex items-start">
                          <span className="text-sm font-medium mr-2" style={{ color: 'var(--color-text-muted)' }}>
                            {formattedNestedKey}:
                          </span>
                          <span className="text-sm flex-1" style={{ color: 'var(--color-text-primary)' }}>
                            {typeof nestedValue === 'object' ? JSON.stringify(nestedValue, null, 2) : String(nestedValue)}
                  </span>
                        </div>
                      );
                    })}
                </div>
                  </div>
              );
            }
          }

          // Handle arrays
          if (Array.isArray(value)) {
            return (
              <div key={key} className="p-3 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                <div className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {formattedKey}
              </div>
                <div className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  {value.length > 0 ? value.join(', ') : 'None'}
        </div>
      </div>
    );
          }

          // Handle simple values
          return (
            <div key={key} className="flex items-start p-3 rounded" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
              <span className="text-sm font-medium mr-2 min-w-[120px]" style={{ color: 'var(--color-text-muted)' }}>
                {formattedKey}:
              </span>
              <span className="text-sm flex-1" style={{ color: 'var(--color-text-primary)' }}>
                {String(value)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const parseLogAction = (log: AuditLog) => {
    const action = log.action || '';
    let details = {};
    
    try {
      details = typeof log.details === 'string' 
        ? (log.details ? JSON.parse(log.details) : {}) 
        : (log.details || {});
    } catch (e) {
      // If parsing fails, use empty object
      details = {};
    }
    
    // Check if it's a CSV import
    const detailsObj = details as Record<string, any>;
    if (action.toLowerCase().includes('csv') || action.toLowerCase().includes('import') || 
        detailsObj['isCsvImport'] || detailsObj['csvImport'] || action.toLowerCase().includes('bulk')) {
      return {
        action: 'CSV Import',
        name: detailsObj['type'] || detailsObj['resourceType'] || log.resource || 'Multiple items',
        isCsvImport: true
      };
    }
    
    // Parse action to determine type
    const actionLower = action.toLowerCase();
    let actionType = 'Updated';
    
    if (actionLower.includes('created') || actionLower.includes('added') || actionLower.includes('add')) {
      actionType = 'Added';
    } else if (actionLower.includes('deleted') || actionLower.includes('removed') || actionLower.includes('delete')) {
      actionType = 'Deleted';
    } else if (actionLower.includes('updated') || actionLower.includes('modified') || actionLower.includes('update')) {
      actionType = 'Updated';
    }
    
    // Extract name from details or resource
    let name = '-';
    if (detailsObj['name']) {
      name = detailsObj['name'];
    } else if (detailsObj['title']) {
      name = detailsObj['title'];
    } else if (detailsObj['locationName']) {
      name = detailsObj['locationName'];
    } else if (detailsObj['providerName']) {
      name = detailsObj['providerName'];
    } else if (detailsObj['conditionName']) {
      name = detailsObj['conditionName'];
    } else if (detailsObj['insuranceName']) {
      name = detailsObj['insuranceName'];
    } else if (detailsObj['appointmentDetailName']) {
      name = detailsObj['appointmentDetailName'];
    } else if (log.resourceId && log.resource) {
      name = `${log.resource} (${log.resourceId})`;
    } else if (log.resource) {
      name = log.resource;
    }
    
    return {
      action: actionType,
      name: name,
      isCsvImport: false
    };
  };

  // Reusable function to render resource management logs (Locations, Conditions, Providers, Insurances, Appointment Details)
  const renderResourceLogs = (data: LogsConnection | undefined, loading: boolean, title: string) => {
    if (loading) return <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>Loading {title.toLowerCase()}...</div>;
    
    const logs: AuditLog[] = data?.nodes || [];
    
    const handleRowClick = (log: AuditLog) => {
      setSelectedLog({ ...log, type: 'action' as const });
      setShowLogModal(true);
    };
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {renderLogHeader(title)}
          {logs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            {renderEmptyState(`No ${title.toLowerCase()} found`)}
          </div>
          ) : (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="overflow-y-auto dark-scrollbar flex-1">
              <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Action</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: AuditLog) => {
                    const parsed = parseLogAction(log);
                    const userEmail = log.user ? log.user.email : 'System';
                    
                    return (
                      <tr
                        key={log.id}
                        onClick={() => handleRowClick(log)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid var(--color-border)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td className="py-3 px-4">
                          <span 
                            className="px-2 py-1 text-xs rounded-full inline-block font-medium"
                            style={{
                              backgroundColor: parsed.action === 'Added' 
                                ? 'var(--color-success-bg)' 
                                : parsed.action === 'Deleted'
                                ? 'var(--color-error-bg)'
                                : parsed.action === 'CSV Import'
                                ? 'rgba(59, 130, 246, 0.2)'
                                : 'var(--color-warning-bg)',
                              color: parsed.action === 'Added' 
                                ? 'var(--color-success)' 
                                : parsed.action === 'Deleted'
                                ? 'var(--color-error)'
                                : parsed.action === 'CSV Import'
                                ? 'var(--color-accent)'
                                : 'var(--color-warning)'
                            }}
                          >
                            {parsed.action}
                          </span>
                        </td>
                        <td className="py-3 px-4" style={{ color: 'var(--color-text-primary)' }}>
                          <span className="font-medium">{parsed.name}</span>
                        </td>
                        <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{userEmail}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {formatTimestamp(log.timestamp)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                  </div>
                </div>
          )}
      </div>
    );
  };


  const renderSystemLogs = () => {
    if (systemLoading) return <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>Loading system logs...</div>;
    
    const logs: SystemLog[] = systemData?.systemLogs?.nodes || [];
    
    const handleRowClick = (log: SystemLog) => {
      setSelectedLog({ ...log, type: 'system' as const });
      setShowLogModal(true);
    };
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {renderLogHeader('System Activity')}
          {logs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            {renderEmptyState('No system logs found')}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="overflow-y-auto dark-scrollbar flex-1">
              <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Source</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Level</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Message</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: SystemLog) => (
                    <tr
                key={log.id} 
                      onClick={() => handleRowClick(log)}
                      className="cursor-pointer transition-colors"
                style={{
                        borderBottom: '1px solid var(--color-border)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {log.source || 'System'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                    <span 
                          className="px-2 py-1 text-xs rounded-full inline-block"
                      style={{
                            backgroundColor: log.level === 'ERROR' || log.level === 'FATAL' 
                              ? 'rgba(239, 68, 68, 0.2)' 
                              : log.level === 'WARN' 
                              ? 'rgba(234, 179, 8, 0.2)' 
                              : 'rgba(59, 130, 246, 0.2)',
                            color: log.level === 'ERROR' || log.level === 'FATAL' 
                              ? 'var(--color-error)' 
                              : log.level === 'WARN' 
                              ? '#eab308' 
                              : 'var(--color-accent)'
                      }}
                    >
                      {log.level}
                    </span>
                      </td>
                      <td className="py-3 px-4" style={{ color: 'var(--color-text-secondary)' }}>
                        {log.message}
                        {log.errorCode && (
                          <span className="text-xs ml-2" style={{ color: 'var(--color-text-muted)' }}>
                            (Error: {log.errorCode})
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {formatTimeAgo(log.timestamp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                  </div>
                </div>
        )}
      </div>
    );
  };


  // Removed unused renderAppointmentDetailsLogs function

  const renderBlockedEvents = () => {
    if (blockedEventsLoading) return <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>Loading blocked events...</div>;
    
    const logs: AuditLog[] = blockedEventsData?.auditLogs?.nodes || [];
    
    const handleRowClick = (log: AuditLog) => {
      setSelectedLog({ ...log, type: 'action' as const });
      setShowLogModal(true);
    };

    // Parse details to extract reason
    const getBlockReason = (log: AuditLog): string => {
      if (log.details && typeof log.details === 'object') {
        const detailsObj = log.details as Record<string, unknown>;
        if (detailsObj['reason']) {
          return String(detailsObj['reason']);
        }
      }
      return 'Access denied by geolocation restriction';
    };
    
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {renderLogHeader('Blocked Accesses')}
        {logs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            {renderEmptyState('No blocked events found')}
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <div className="overflow-y-auto dark-scrollbar flex-1">
              <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>IP Address</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>User</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Reason</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: AuditLog) => {
                    const userEmail = log.user ? log.user.email : 'Anonymous';
                    const reason = getBlockReason(log);
                    
                    return (
                      <tr
                        key={log.id}
                        onClick={() => handleRowClick(log)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid var(--color-border)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <Ban className="w-4 h-4" style={{ color: 'var(--color-error)' }} />
                            <span className="font-mono text-sm" style={{ color: 'var(--color-text-primary)' }}>
                              {log.ipAddress || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {userEmail}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4" style={{ color: 'var(--color-text-primary)' }}>
                          <span className="text-sm">{reason}</span>
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {formatTimestamp(log.timestamp)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLogContent = () => {
    switch (activeLogTab) {
      case 'user-activity':
        return renderUserActivity();
      case 'blocked-events':
        return renderBlockedEvents();
      case 'locations':
        return renderResourceLogs(locationData?.locationLogs, locationLoading, 'Location Management Activity');
      case 'appointment-details':
        return renderResourceLogs(undefined, false, 'Appointment Details Activity');
      case 'conditions':
        return renderResourceLogs(conditionData?.conditionLogs, conditionLoading, 'Condition Management Activity');
      case 'providers':
        return renderResourceLogs(providerData?.providerLogs, providerLoading, 'Provider Management Activity');
      case 'insurances':
        return renderResourceLogs(insuranceData?.insuranceLogs, insuranceLoading, 'Insurance Management Activity');
      case 'system':
        return renderSystemLogs();
      case 'retention':
        return <LogRetentionSettings />;
      default:
        return <div>Select a log category</div>;
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
      {/* Log Category Navigation */}
      <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--color-border)' }}>
        <nav className="flex space-x-2 md:space-x-8 overflow-x-auto dark-scrollbar py-4 flex-wrap md:flex-nowrap">
          {logTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveLogTab(tab.id)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                  activeLogTab === tab.id
                    ? 'shadow-sm'
                    : 'hover:bg-opacity-20'
                }`}
                style={{
                  backgroundColor: activeLogTab === tab.id 
                    ? 'var(--color-accent)' 
                    : 'transparent',
                  color: activeLogTab === tab.id 
                    ? 'white' 
                    : 'var(--color-text-secondary)',
                  '--hover-bg-opacity': '0.2'
                } as React.CSSProperties}
                onMouseEnter={(e) => {
                  if (activeLogTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeLogTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Log Content */}
      <div 
        className={`flex-1 overflow-hidden rounded-xl shadow-sm border ${activeLogTab === 'retention' ? 'overflow-y-auto p-6' : 'p-4 md:p-6'}`}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
          {renderLogContent()}
      </div>

      {/* Log Details Modal - Rendered at component level so it works for all tabs */}
      {showLogModal && selectedLog && (
        <>
          <style>{DARK_SCROLLBAR_STYLES}</style>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowLogModal(false)}
          >
            <div 
              className="rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto log-modal-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border)',
                boxShadow: 'var(--shadow-xl)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Activity Details</h3>
                <button
                  onClick={() => setShowLogModal(false)}
                  className="transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                  }}
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>User</label>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {selectedLog.type === 'login' 
                      ? selectedLog.email 
                      : selectedLog.type === 'action' && selectedLog.user
                      ? selectedLog.user.email
                      : 'System'}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Activity</label>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {selectedLog.type === 'login' 
                      ? (selectedLog.success ? 'Successful login' : `Failed login${selectedLog.failureCode ? ` (${selectedLog.failureCode})` : ''}`)
                      : selectedLog.type === 'action'
                      ? selectedLog.action
                      : selectedLog.type === 'system'
                      ? selectedLog.message
                      : 'Unknown'}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Timestamp</label>
                  <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                    {formatTimestamp(selectedLog.timestamp)}
                  </p>
                </div>
                
                {selectedLog.type === 'login' && selectedLog.method && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Login Method</label>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                      {selectedLog.method === 'entra-id' ? 'Entra ID' : selectedLog.method === 'password' ? 'Local' : selectedLog.method}
                    </p>
                  </div>
                )}
                
                {(selectedLog.type === 'login' || selectedLog.type === 'action') && selectedLog.ipAddress && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>IP Address</label>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>{selectedLog.ipAddress}</p>
                  </div>
                )}
                
                {(selectedLog.type === 'login' || selectedLog.type === 'action') && selectedLog.userAgent && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>User Agent</label>
                    <p className="mt-1 text-sm break-all" style={{ color: 'var(--color-text-primary)' }}>{selectedLog.userAgent}</p>
                  </div>
                )}
                
                {selectedLog.type === 'action' && selectedLog.resource && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Resource</label>
                    <p className="mt-1" style={{ color: 'var(--color-text-primary)' }}>
                      {selectedLog.resource}{selectedLog.resourceId && ` (ID: ${selectedLog.resourceId})`}
                    </p>
                  </div>
                )}
                
                {(selectedLog.type === 'action' || selectedLog.type === 'system') && selectedLog.details && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Details</label>
                    {formatDetails(selectedLog.details)}
                  </div>
                )}
                
                {selectedLog.type === 'login' && selectedLog.failureCode && (
                  <div>
                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Failure Code</label>
                    <p className="mt-1" style={{ color: 'var(--color-error)' }}>{selectedLog.failureCode}</p>
                  </div>
                )}
              </div>
        </div>
      </div>
          </div>
        </>
      )}
    </div>
  );
};
